import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { attemptTokenRefreshOnReconnect } from 'common/infrastructure/api/http-client';

type OnlineStatusContextType = {
  isOnline: boolean;
};

export const OnlineStatusContext = createContext<OnlineStatusContextType | null>(null);

type OnlineStatusProviderProps = {
  children: ReactNode;
};

export function OnlineStatusProvider({ children }: OnlineStatusProviderProps) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      void attemptTokenRefreshOnReconnect();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <OnlineStatusContext.Provider value={{ isOnline }}>{children} </OnlineStatusContext.Provider>
  );
}

export const useOnlineStatus = () => {
  const context = useContext(OnlineStatusContext);

  if (!context) {
    throw new Error('useOnlineStatus must be used within an OnlineStatusProvider');
  }

  return context;
};
